import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { RefreshCw, Trash2, Search, Loader2, FileText, Film, ChevronDown, ChevronRight, Bot, Play, XCircle } from 'lucide-react'
import RobotsApplyModal from './RobotsApplyModal'
import { useHighlightsApi } from './useHighlightsApi'
import TranscriptViewer from './TranscriptViewer'
import VideoReelComposer from './VideoReelComposer'
import ClipPreview from './ClipPreview'
import { dedupAiKeywordAgainstLlm } from './dedupMoments'
import { useReject } from './useReject'
import { RejectMomentDialog, RejectUndoToast } from './RejectMomentDialog'
import { useApprove } from './useApprove'
import { ApproveUndoToast } from './ApproveMomentControl'
import { CheckCircle, Plus } from 'lucide-react'
import AddMissingMomentDialog from './AddMissingMomentDialog'

const STATUS_PILLS = {
  none:       { dot: '⚪', label: 'Not enabled',   color: '#888' },
  requested:  { dot: '🟡', label: 'Requested',     color: '#d4a72c' },
  preparing:  { dot: '🟠', label: 'Mux processing', color: '#e07b00' },
  ready:      { dot: '🔵', label: 'Indexing…',     color: '#3b82f6' },
  indexed:    { dot: '🟢', label: 'Indexed',       color: '#22c55e' },
  errored:    { dot: '🔴', label: 'Failed',        color: '#dc2626' },
}
const NON_TERMINAL = new Set(['requested', 'preparing', 'ready'])
const PAGE_SIZE = 50

function fmtRelative(iso) {
  if (!iso) return ''
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z')
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return `${Math.round(diff)}s ago`
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`
  return `${Math.round(diff / 86400)}d ago`
}

function fmtDuration(sec) {
  if (!sec) return ''
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${s.toString().padStart(2, '0')}s`
}

export default function CaptionLibrary() {
  const api = useHighlightsApi()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('') // '' | 'none' | 'errored' | 'indexed' | 'in_progress'
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [busyIds, setBusyIds] = useState(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [errors, setErrors] = useState([]) // [{ tbtv_id, error, message, op }]
  const [viewing, setViewing] = useState(null) // tbtv_id whose transcript is open in the modal
  const [composing, setComposing] = useState(null) // { tbtv_id, title } whose reel composer is open
  const [runningAi, setRunningAi] = useState(null) // tbtv_id whose detectors-apply modal is open
  const [expandedSummary, setExpandedSummary] = useState(() => new Set()) // tbtv_ids whose summary row is open
  const [expandedKm, setExpandedKm] = useState(() => new Set())            // tbtv_ids whose KM row is open
  const [expandedCh, setExpandedCh] = useState(() => new Set())            // tbtv_ids whose chapters row is open
  const [previewing, setPreviewing] = useState(null)                       // { tbtv_id, startMs, endMs?, title } | null
  // Per-video caches for the on-demand fetches. Keyed by tbtv_id; value is
  // { loading, error, data }. Only fetch once per video per session; the
  // user can re-collapse + re-expand without re-firing the request.
  const [kmCache, setKmCache] = useState(() => new Map())
  const [chCache, setChCache] = useState(() => new Map())

  const load = useCallback(async ({ offset: ofs = 0, q: query = q, status = statusFilter } = {}) => {
    setLoading(true)
    setErr(null)
    try {
      const statusParam =
        status === 'in_progress' ? 'requested,preparing,ready' :
        status || undefined
      const res = await api.library({ status: statusParam, q: query, limit: PAGE_SIZE, offset: ofs })
      setItems(res.items || [])
      setTotal(res.total ?? 0)
      setOffset(ofs)
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [api, q, statusFilter])

  useEffect(() => { load({ offset: 0 }) }, [])

  // 30s status poll while any visible row is non-terminal.
  const pollTimer = useRef(null)
  useEffect(() => {
    const nonTerminal = items.filter(i => NON_TERMINAL.has(i.caption_status))
    if (nonTerminal.length === 0) {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null }
      return
    }
    pollTimer.current = setInterval(async () => {
      try {
        const ids = nonTerminal.map(i => i.tbtv_id)
        const res = await api.captionStatus(ids)
        const updates = res.statuses || {}
        setItems(prev => prev.map(it => {
          const u = updates[it.tbtv_id]
          if (!u || u.status == null) return it
          return {
            ...it,
            caption_status: u.status,
            cue_count: u.cue_count ?? it.cue_count,
            word_count: u.word_count ?? it.word_count,
            requested_at: u.requested_at || it.requested_at,
            ready_at: u.ready_at || it.ready_at,
            indexed_at: u.indexed_at || it.indexed_at,
            error_message: u.error_message || it.error_message,
          }
        }))
      } catch (e) {
        console.warn('caption status poll failed', e)
      }
    }, 30000)
    return () => { if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null } }
  }, [items, api])

  const pushError = (entries) => setErrors(prev => [...entries, ...prev].slice(0, 50))

  async function enableOne(tbtv_id) {
    setBusyIds(prev => new Set(prev).add(tbtv_id))
    try {
      const r = await api.enableCaptions([tbtv_id])
      const result = (r.results || [])[0]
      if (result && !result.ok) {
        pushError([{ tbtv_id, op: 'enable', error: result.error, message: result.message }])
      }
      setItems(prev => prev.map(it =>
        it.tbtv_id === tbtv_id
          ? { ...it, caption_status: result?.ok ? (result.status || 'requested') : it.caption_status }
          : it
      ))
    } catch (e) {
      pushError([{ tbtv_id, op: 'enable', error: 'request_failed', message: e.message }])
    } finally {
      setBusyIds(prev => { const n = new Set(prev); n.delete(tbtv_id); return n })
    }
  }

  async function refreshOne(tbtv_id) {
    setBusyIds(prev => new Set(prev).add(tbtv_id))
    try {
      await api.refresh(tbtv_id)
      const res = await api.captionStatus([tbtv_id])
      const u = res.statuses?.[tbtv_id]
      if (u) {
        setItems(prev => prev.map(it => it.tbtv_id === tbtv_id ? { ...it, caption_status: u.status || 'requested' } : it))
      }
    } catch (e) {
      pushError([{ tbtv_id, op: 'refresh', error: 'request_failed', message: e.message }])
    } finally {
      setBusyIds(prev => { const n = new Set(prev); n.delete(tbtv_id); return n })
    }
  }

  async function deleteOne(tbtv_id) {
    if (!confirm(`Delete cached captions for ${tbtv_id}?\n\nThis purges cues from D1 but does NOT remove the track from Mux. You can re-enable later.`)) return
    setBusyIds(prev => new Set(prev).add(tbtv_id))
    try {
      await api.deleteCaptions(tbtv_id)
      setItems(prev => prev.map(it =>
        it.tbtv_id === tbtv_id ? { ...it, caption_status: 'none', cue_count: null, word_count: null, error_message: null } : it
      ))
    } catch (e) {
      pushError([{ tbtv_id, op: 'delete', error: 'request_failed', message: e.message }])
    } finally {
      setBusyIds(prev => { const n = new Set(prev); n.delete(tbtv_id); return n })
    }
  }

  async function bulkEnable() {
    if (selected.size === 0) return
    setBulkBusy(true)
    try {
      const ids = Array.from(selected)
      const r = await api.enableCaptions(ids)
      const byId = new Map((r.results || []).map(x => [x.tbtv_id, x]))
      setItems(prev => prev.map(it => {
        const x = byId.get(it.tbtv_id)
        if (!x || !x.ok) return it
        return { ...it, caption_status: x.status || 'requested' }
      }))
      const failed = (r.results || []).filter(x => !x.ok)
      if (failed.length > 0) {
        console.error('bulk_enable_errors', failed)
        pushError(failed.map(e => ({
          tbtv_id: e.tbtv_id, op: 'enable', error: e.error, message: e.message,
        })))
      }
      setSelected(new Set())
    } catch (e) {
      pushError([{ tbtv_id: '(bulk)', op: 'enable', error: 'request_failed', message: e.message }])
    } finally {
      setBulkBusy(false)
    }
  }

  function copyErrors() {
    const text = errors.map(e =>
      `${e.tbtv_id}  [${e.op}]\n  error: ${e.error}\n  message: ${e.message || '(none)'}`
    ).join('\n\n')
    navigator.clipboard?.writeText(text).then(
      () => console.log('errors copied to clipboard'),
      () => alert('clipboard copy blocked — select the text manually')
    )
  }

  // Generic open/close + lazy-fetch helper for the KM/chapter row expansions.
  // First open triggers the fetch; subsequent open/close just toggles the Set.
  // Errors are surfaced inside the expanded panel itself; never blocking.
  const openExpanded = (tbtv_id, expandedSet, setExpandedSet, cache, setCache, fetcher) => {
    setExpandedSet(prev => {
      const next = new Set(prev)
      if (next.has(tbtv_id)) {
        next.delete(tbtv_id)
        return next
      }
      next.add(tbtv_id)
      // Fire fetch only if we haven't cached a successful response.
      const entry = cache.get(tbtv_id)
      if (!entry || (!entry.data && !entry.loading)) {
        setCache(c => {
          const m = new Map(c)
          m.set(tbtv_id, { loading: true, error: null, data: null })
          return m
        })
        fetcher(tbtv_id).then(
          (data) => setCache(c => {
            const m = new Map(c)
            m.set(tbtv_id, { loading: false, error: null, data })
            return m
          }),
          (err) => setCache(c => {
            const m = new Map(c)
            m.set(tbtv_id, { loading: false, error: err.message || 'fetch failed', data: null })
            return m
          })
        )
      }
      return next
    })
  }

  const toggleKm = (tbtv_id) => {
    openExpanded(
      tbtv_id, expandedKm, setExpandedKm, kmCache, setKmCache,
      // Step 25g: suppress AI-keyword moments that fall inside an AI-LLM
      // moment's span — same play, two rows otherwise.
      (id) => api.keyMoments(id).then(r => dedupAiKeywordAgainstLlm(r?.moments || [])),
    )
  }
  const toggleCh = (tbtv_id) => {
    openExpanded(
      tbtv_id, expandedCh, setExpandedCh, chCache, setChCache,
      (id) => api.chapters(id).then(r => r?.chapters || []),
    )
  }

  // Step 26.5: editor reject available from the expanded Key Moments view.
  // On local remove, splice the moment out of kmCache so it vanishes
  // immediately. On restore (Undo), re-fetch is simplest — but for UX
  // we just put it back into the cache entry's data array.
  const reject = useReject({
    onLocalRemove: (m) => {
      setKmCache(prev => {
        const next = new Map(prev)
        const entry = next.get(m.tbtv_id)
        if (entry && Array.isArray(entry.data)) {
          next.set(m.tbtv_id, { ...entry, data: entry.data.filter(x => !(x.start_ms === m.start_ms && (x.title || '') === (m.title || ''))) })
        }
        return next
      })
      // Step 27 UI option B: bump coverage counts optimistically.
      setDroppedCounts(prev => ({ ...prev, [m.tbtv_id]: (prev[m.tbtv_id] || 0) + 1 }))
    },
    onLocalRestore: (m) => {
      setKmCache(prev => {
        const next = new Map(prev)
        const entry = next.get(m.tbtv_id)
        if (entry && Array.isArray(entry.data)) {
          // Re-insert and re-sort by start_ms.
          const restored = [...entry.data, m].sort((a, b) => a.start_ms - b.start_ms)
          next.set(m.tbtv_id, { ...entry, data: restored })
        }
        return next
      })
      setDroppedCounts(prev => ({ ...prev, [m.tbtv_id]: Math.max(0, (prev[m.tbtv_id] || 0) - 1) }))
    },
  })

  // Step 27 Option B: track approvals so the inline ✓ badge persists
  // across page refresh. Seed from server on mount via the new
  // /admin/approved-moments/fingerprints endpoint, then bump locally
  // on each approve/undo for instant UI feedback.
  const [locallyApprovedSet, setLocallyApprovedSet] = useState(() => new Set())
  const approveFingerprint = (m) => `${m.tbtv_id}|${m.start_ms}|${(m.title || '').trim().toLowerCase()}`
  useEffect(() => {
    let cancelled = false
    api.approvedMomentsFingerprints().then(r => {
      if (cancelled) return
      const seeded = new Set()
      for (const f of (r?.fingerprints || [])) {
        seeded.add(`${f.tbtv_id}|${f.start_ms}|${(f.title || '').trim().toLowerCase()}`)
      }
      setLocallyApprovedSet(seeded)
    }).catch(e => console.warn('failed to seed approved fingerprints', e))
    return () => { cancelled = true }
  }, [api])

  // Step 27 UI option B: per-video coverage counts (approved + rejected).
  // Single fetch on mount; bumped optimistically on each approve/reject.
  // Map shape: { tbtv_id: count }
  const [approvedCounts, setApprovedCounts] = useState({})
  const [droppedCounts, setDroppedCounts] = useState({})
  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.approvedMomentsCounts().catch(() => ({ counts: {} })),
      api.droppedMomentsCounts().catch(() => ({ counts: {} })),
    ]).then(([a, d]) => {
      if (cancelled) return
      setApprovedCounts(a?.counts || {})
      setDroppedCounts(d?.counts || {})
    })
    return () => { cancelled = true }
  // Re-fetch when offset changes (page navigation). Also a manual refresh
  // when an approve / reject lands (handled in the hook callbacks below).
  }, [api])

  const approve = useApprove({
    onLocalMark: (m) => {
      setLocallyApprovedSet(prev => new Set(prev).add(approveFingerprint(m)))
      setApprovedCounts(prev => ({ ...prev, [m.tbtv_id]: (prev[m.tbtv_id] || 0) + 1 }))
    },
    onLocalUnmark: (m) => {
      setLocallyApprovedSet(prev => { const n = new Set(prev); n.delete(approveFingerprint(m)); return n })
      setApprovedCounts(prev => ({ ...prev, [m.tbtv_id]: Math.max(0, (prev[m.tbtv_id] || 0) - 1) }))
    },
  })

  // Step 27 follow-up: "+ Add missing moment" — for moments the LLM
  // didn't catch but should have. addingMissingFor holds the {tbtv_id,
  // title} we're adding for; setting it opens the dialog.
  const [addingMissingFor, setAddingMissingFor] = useState(null)
  async function submitMissingMoment(syntheticMoment) {
    // Reuse the approve hook — it POSTs to /admin/approved-moments and
    // wires up the undo toast. Same UX as a regular approve, but the
    // moment payload was authored manually instead of pulled from an
    // existing LLM emission.
    await approve.start(syntheticMoment)
    setAddingMissingFor(null)
  }

  const toggleSelect = (tbtv_id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(tbtv_id)) next.delete(tbtv_id); else next.add(tbtv_id)
      return next
    })
  }
  // Only select rows whose backend is implemented (v1: 'mux'). CF-Stream
  // and unsupported rows are skipped so bulk-enable never tries to call a
  // provider that returns 501.
  const selectableIds = items.filter(i => i.backend === 'mux').map(i => i.tbtv_id)
  const toggleSelectAll = () => {
    setSelected(prev => {
      const allSelected = selectableIds.length > 0 && selectableIds.every(id => prev.has(id))
      return allSelected ? new Set() : new Set(selectableIds)
    })
  }

  const pillFor = (it) => {
    const pill = STATUS_PILLS[it.caption_status] || STATUS_PILLS.none
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: pill.color, fontSize: 13, fontWeight: 500 }}>
        <span>{pill.dot}</span>
        <span>{pill.label}</span>
        {it.caption_status === 'indexed' && it.cue_count != null && (
          <span style={{ color: '#888', fontWeight: 400 }}>· {it.cue_count.toLocaleString()} cues</span>
        )}
        {it.caption_status === 'requested' && it.requested_at && (
          <span style={{ color: '#888', fontWeight: 400 }}>· {fmtRelative(it.requested_at)}</span>
        )}
      </span>
    )
  }

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <form onSubmit={(e) => { e.preventDefault(); load({ offset: 0 }) }} style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 240 }}>
          <Search size={16} style={{ color: '#888' }} />
          <input
            type="text"
            placeholder="Filter by title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ flex: 1, padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
          />
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { const v = e.target.value; setStatusFilter(v); load({ offset: 0, status: v }) }}
          style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
        >
          <option value="">All statuses</option>
          <option value="none">⚪ Not enabled</option>
          <option value="in_progress">🟡 In progress</option>
          <option value="indexed">🟢 Indexed</option>
          <option value="errored">🔴 Errored</option>
        </select>
        <button
          onClick={() => load({ offset })}
          disabled={loading}
          style={{ padding: '6px 12px', background: '#222', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer' }}
        >
          {loading ? <Loader2 size={14} className="spin" /> : 'Refresh'}
        </button>
      </div>

      {/* Error panel — copyable, dismissible */}
      {errors.length > 0 && (
        <div className="card" style={{ marginBottom: 12, background: '#2a1414', border: '1px solid #5a1f1f' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ color: '#fca5a5' }}>{errors.length} error{errors.length === 1 ? '' : 's'}</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={copyErrors}
                style={{ padding: '4px 10px', background: '#1f1f1f', border: '1px solid #444', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12 }}>
                Copy all
              </button>
              <button onClick={() => setErrors([])}
                style={{ padding: '4px 10px', background: 'transparent', border: '1px solid #444', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12 }}>
                Dismiss
              </button>
            </div>
          </div>
          <pre style={{
            margin: 0, fontSize: 12, lineHeight: 1.5, fontFamily: 'ui-monospace, monospace',
            maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text',
          }}>
{errors.map((e, i) => (
`${e.tbtv_id}  [${e.op}]
  error:   ${e.error}
  message: ${e.message || '(none)'}`
)).join('\n\n')}
          </pre>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="card" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1a2538' }}>
          <span>{selected.size} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={bulkEnable}
              disabled={bulkBusy}
              style={{ padding: '6px 14px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontWeight: 500 }}
            >
              {bulkBusy ? 'Enabling…' : `Enable captions on ${selected.size}`}
            </button>
            <button
              onClick={() => setSelected(new Set())}
              style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer' }}
            >Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {err && (
          <div style={{ padding: 16, color: 'crimson' }}>{err}</div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#161616', borderBottom: '1px solid #2a2a2a' }}>
            <tr>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 32 }}>
                <input
                  type="checkbox"
                  checked={selectableIds.length > 0 && selectableIds.every(id => selected.has(id))}
                  onChange={toggleSelectAll}
                  disabled={selectableIds.length === 0}
                  title={selectableIds.length === 0 ? 'No Mux-backed rows on this page' : 'Select all Mux-backed rows'}
                />
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>Title</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 70 }}>Backend</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 260 }}>Captions</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 90 }} title="Mux Robots: Find Key Moments">Key moments</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 80 }} title="Mux Robots: Generate Chapters">Chapters</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 90 }} title="Mux Robots: Summarize — auto-generated title + description + tags">Summary</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', width: 100 }}
                title="Editorial coverage — ✓ Approved vs ✗ Rejected moments for this video. Feeds the Step 27 eval harness + future Step 29 RAG.">
                Coverage
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'right', width: 240 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>No active content.</td></tr>
            )}
            {items.map(it => {
              const isBusy = busyIds.has(it.tbtv_id)
              const isSelected = selected.has(it.tbtv_id)
              const muxOnly = it.backend === 'mux'
              return (
                <Fragment key={it.tbtv_id}>
                <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(it.tbtv_id)}
                      disabled={!muxOnly}
                      title={
                        muxOnly
                          ? ''
                          : it.backend === 'cf_stream'
                            ? "Disabled — backend 'cf_stream' isn't implemented in v1. Re-upload as a Mux asset to enable."
                            : "Disabled — no backend reference (mux_asset_id and stream_uid are both null). Upload to Mux first, or remove this content row from the catalog."
                      }
                    />
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {it.thumbnail_url ? (
                        <img src={it.thumbnail_url} alt="" style={{ width: 48, height: 27, objectFit: 'cover', borderRadius: 2, background: '#222', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 48, height: 27, background: '#222', borderRadius: 2, flexShrink: 0 }} />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480 }}>{it.title || '(untitled)'}</div>
                        <div style={{ fontSize: 11, color: '#777', fontFamily: 'monospace' }}>
                          {it.tbtv_id} {it.duration ? `· ${fmtDuration(it.duration)}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#888' }}>{it.backend || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {pillFor(it)}
                    {it.error_message && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.error_message}>
                        {it.error_message}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>
                    <RobotCell
                      count={it.key_moment_count}
                      jobStatus={it.fkm_job_status}
                      completedIcon="💫"
                      title={it.key_moment_best_score != null ? `Best score: ${it.key_moment_best_score.toFixed(2)}` : undefined}
                      expanded={expandedKm.has(it.tbtv_id)}
                      onToggle={() => toggleKm(it.tbtv_id)}
                      extra={
                        <>
                          {it.key_moment_best_score != null && it.key_moment_count > 0
                            ? <span style={{ color: '#888', marginLeft: 4 }}>· {it.key_moment_best_score.toFixed(2)}</span>
                            : null}
                          <SourceBreakdown by={it.key_moment_count_by_source} variant="km" />
                        </>
                      }
                    />
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>
                    <RobotCell
                      count={it.chapter_count}
                      jobStatus={it.chapters_job_status}
                      completedIcon="📑"
                      expanded={expandedCh.has(it.tbtv_id)}
                      onToggle={() => toggleCh(it.tbtv_id)}
                      extra={<SourceBreakdown by={it.chapter_count_by_source} variant="ch" />}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>
                    <SummaryCell
                      summary={it.summary}
                      jobStatus={it.summarize_job_status}
                      expanded={expandedSummary.has(it.tbtv_id)}
                      onToggle={() => setExpandedSummary(prev => {
                        const n = new Set(prev)
                        if (n.has(it.tbtv_id)) n.delete(it.tbtv_id); else n.add(it.tbtv_id)
                        return n
                      })}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12 }}>
                    <CoverageCell
                      approved={approvedCounts[it.tbtv_id] || 0}
                      dropped={droppedCounts[it.tbtv_id] || 0}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {it.caption_status === 'none' && muxOnly && (
                        <button onClick={() => enableOne(it.tbtv_id)} disabled={isBusy}
                          style={{ padding: '4px 12px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontSize: 12 }}>
                          {isBusy ? 'Enabling…' : 'Enable'}
                        </button>
                      )}
                      {it.caption_status === 'errored' && (
                        <button onClick={() => enableOne(it.tbtv_id)} disabled={isBusy}
                          style={{ padding: '4px 12px', background: '#dc2626', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontSize: 12 }}>
                          Retry
                        </button>
                      )}
                      {it.caption_status === 'indexed' && (
                        <button onClick={() => setViewing(it.tbtv_id)} title="View captions"
                          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FileText size={12} /> View
                        </button>
                      )}
                      {(it.key_moment_count > 0 || it.chapter_count > 0) && (
                        <button
                          onClick={() => setComposing({ tbtv_id: it.tbtv_id, title: it.title })}
                          title="Build a reel from this video's key moments + chapters"
                          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #3b82f6', borderRadius: 4, color: '#3b82f6', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Film size={12} /> Reel
                        </button>
                      )}
                      {it.caption_status === 'indexed' && (
                        <button onClick={() => setRunningAi(it.tbtv_id)} title="Apply Mux Robots or TBTV-AI detectors to this video"
                          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #a78bfa', borderRadius: 4, color: '#a78bfa', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Bot size={12} /> Run AI
                        </button>
                      )}
                      {it.caption_status !== 'none' && (
                        <>
                          <button onClick={() => refreshOne(it.tbtv_id)} disabled={isBusy} title="Re-fetch from Mux"
                            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <RefreshCw size={12} className={isBusy ? 'spin' : ''} /> Refresh
                          </button>
                          <button onClick={() => deleteOne(it.tbtv_id)} disabled={isBusy} title="Delete cached cues from D1"
                            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#dc2626', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedSummary.has(it.tbtv_id) && it.summary && (
                  <tr style={{ background: '#0d1622', borderBottom: '1px solid #1f1f1f' }}>
                    <td colSpan={9} style={{ padding: '12px 16px 16px 60px' }}>
                      <ExpandedSummary summary={it.summary} />
                    </td>
                  </tr>
                )}
                {expandedKm.has(it.tbtv_id) && (
                  <tr style={{ background: '#0d1622', borderBottom: '1px solid #1f1f1f' }}>
                    <td colSpan={9} style={{ padding: '12px 16px 16px 60px' }}>
                      <ExpandedKeyMoments
                        entry={kmCache.get(it.tbtv_id)}
                        tbtv_id={it.tbtv_id}
                        tbtvId={it.tbtv_id}
                        videoTitle={it.title}
                        onPreview={(m) => setPreviewing({
                          tbtv_id: it.tbtv_id,
                          startMs: m.start_ms,
                          endMs: m.end_ms,
                          title: m.title || it.title,
                          moment: { ...m, tbtv_id: m.tbtv_id || it.tbtv_id },
                        })}
                        onReject={(m) => reject.start(m)}
                        onApprove={(m) => approve.start({ ...m, tbtv_id: m.tbtv_id || it.tbtv_id })}
                        approvedSet={locallyApprovedSet}
                        onAddMissing={() => setAddingMissingFor({ tbtvId: it.tbtv_id, videoTitle: it.title })}
                      />
                    </td>
                  </tr>
                )}
                {expandedCh.has(it.tbtv_id) && (
                  <tr style={{ background: '#0d1622', borderBottom: '1px solid #1f1f1f' }}>
                    <td colSpan={9} style={{ padding: '12px 16px 16px 60px' }}>
                      <ExpandedChapters
                        entry={chCache.get(it.tbtv_id)}
                        tbtv_id={it.tbtv_id}
                        videoTitle={it.title}
                        onPreview={(c) => setPreviewing({
                          tbtv_id: it.tbtv_id,
                          startMs: c.start_ms,
                          // Chapters don't carry end_ms in our schema; use 20s
                          // as a reasonable navigational preview window.
                          endMs: c.start_ms + 20000,
                          title: c.title || it.title,
                        })}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, color: '#888', fontSize: 13 }}>
        <span>
          {total > 0 && `Showing ${offset + 1}–${Math.min(offset + items.length, total)} of ${total}`}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => load({ offset: Math.max(0, offset - PAGE_SIZE) })}
            disabled={offset === 0 || loading}
            style={{ padding: '4px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer' }}
          >← Prev</button>
          <button
            onClick={() => load({ offset: offset + PAGE_SIZE })}
            disabled={offset + items.length >= total || loading}
            style={{ padding: '4px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer' }}
          >Next →</button>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; } @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } } .pulse { animation: pulse 1.5s ease-in-out infinite; }`}</style>

      {viewing && (
        <TranscriptViewer
          tbtv_id={viewing}
          language="en"
          onClose={() => setViewing(null)}
        />
      )}

      {previewing && (
        <ClipPreview
          tbtv_id={previewing.tbtv_id}
          startMs={previewing.startMs}
          endMs={previewing.endMs}
          title={previewing.title}
          moment={previewing.moment}
          onReject={previewing.moment ? () => reject.start(previewing.moment) : null}
          onApprove={previewing.moment ? () => approve.start(previewing.moment) : null}
          onClose={() => setPreviewing(null)}
        />
      )}

      {/* Step 26.5: shared reject dialog + undo toast for the Caption Library. */}
      <RejectMomentDialog hook={reject} />
      <RejectUndoToast hook={reject} />
      {/* Step 27 Option B: approve undo toast. */}
      <ApproveUndoToast hook={approve} offsetBottom={80} />
      {/* Step 27 follow-up: add-missing-moment dialog. */}
      {addingMissingFor && (
        <AddMissingMomentDialog
          tbtvId={addingMissingFor.tbtvId}
          videoTitle={addingMissingFor.videoTitle}
          onCancel={() => setAddingMissingFor(null)}
          onSubmit={submitMissingMoment}
        />
      )}

      {runningAi && (
        <RobotsApplyModal
          preselectTbtvIds={[runningAi]}
          onClose={() => {
            setRunningAi(null)
            // Refresh row so new TBTV-AI counts / job statuses show.
            load({ offset })
          }}
        />
      )}

      {composing && (
        <VideoReelComposer
          tbtv_id={composing.tbtv_id}
          title={composing.title}
          onClose={() => setComposing(null)}
          onCreated={(reel) => {
            // Brief in-page confirmation; full UX (status, playback) lives
            // in the Reels tab. We let the user discover it there.
            console.log('reel_created_from_video_composer', reel)
          }}
        />
      )}
    </div>
  )
}

// Cell for the Summary column. Shows expand/collapse for the inline detail
// row underneath. States:
//   - has summary       → 📝 N words · ⇣ (clickable to expand)
//   - in flight         → 🟡 in flight (pulsing)
//   - errored           → 🔴 errored
//   - none              → —
function SummaryCell({ summary, jobStatus, expanded, onToggle }) {
  const inFlight = jobStatus === 'pending' || jobStatus === 'processing'
  const errored = jobStatus === 'errored' && !summary

  if (summary) {
    const wordCount = (summary.description || '').split(/\s+/).filter(Boolean).length
    const sources = summary.by_source ? Object.keys(summary.by_source) : []
    return (
      <button onClick={onToggle}
        title={expanded ? 'Hide summary' : 'Show summary'}
        style={{ background: 'transparent', border: 'none', color: '#bfdbfe', cursor: 'pointer', padding: 0, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        📝 {wordCount}{wordCount === 1 ? ' word' : ' words'}
        {sources.length > 0 && (
          <span style={{ display: 'inline-flex', gap: 3 }}>
            {sources.includes('mux') && (
              <span title="Mux Summarize wrote a summary for this video" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, background: 'rgba(251,146,60,0.15)', color: '#fb923c', fontWeight: 600, cursor: 'help' }}>M</span>
            )}
            {sources.includes('tbtv_ai') && (
              <span title="TBTV-AI Claude wrote a summary for this video" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600, cursor: 'help' }}>A</span>
            )}
          </span>
        )}
      </button>
    )
  }
  if (inFlight) {
    return <span className="pulse" style={{ color: '#d4a72c' }}>🟡 in flight</span>
  }
  if (errored) {
    return <span style={{ color: '#dc2626' }}>🔴 errored</span>
  }
  return <span style={{ color: '#555' }}>—</span>
}

// One cell for a Robot column in the Caption Library. Shows:
//   - count + completed icon when there are stored results
//   - "🟡 in flight · pulse" when a job is pending or processing
//   - "🔴 errored" when the most-recent job errored AND there are no results
//   - "—" when nothing has been requested
function RobotCell({ count, jobStatus, completedIcon, title, extra, expanded, onToggle }) {
  const inFlight = jobStatus === 'pending' || jobStatus === 'processing'
  const errored = jobStatus === 'errored' && (count ?? 0) === 0

  if (count > 0) {
    const interactive = typeof onToggle === 'function'
    const body = (
      <>
        <span>{completedIcon} {count.toLocaleString()}</span>
        {extra}
        {interactive && (
          <span style={{ marginLeft: 6, color: '#7dd3fc', fontSize: 11 }}>
            {expanded ? '⇡' : '⇣'}
          </span>
        )}
        {inFlight && (
          <span className="pulse" title="A re-run is in flight"
            style={{ marginLeft: 6, color: '#d4a72c', fontSize: 11 }}>🟡 re-run</span>
        )}
      </>
    )
    if (!interactive) return <span title={title}>{body}</span>
    return (
      <button
        onClick={onToggle}
        title={expanded ? 'Hide details' : (title || 'Show details')}
        style={{
          background: 'transparent', border: 'none', padding: 0,
          color: 'inherit', cursor: 'pointer', font: 'inherit', textAlign: 'left',
        }}>
        {body}
      </button>
    )
  }
  if (inFlight) {
    return (
      <span className="pulse" title="Mux is processing this Robot job" style={{ color: '#d4a72c' }}>
        🟡 in flight
      </span>
    )
  }
  if (errored) {
    return (
      <span title="Last Robot attempt errored — click Apply Robots to retry" style={{ color: '#dc2626' }}>
        🔴 errored
      </span>
    )
  }
  return <span style={{ color: '#555' }}>—</span>
}

// Inline source-breakdown pills shown alongside the Key Moments / Chapters
// counts in each row. Reads the by_source map the worker returns. Color
// scheme matches the chip strip in the Key Moments tab so editors who've
// seen it once recognize the source by color:
//   M (orange) = Mux Robots
//   K (purple) = TBTV-AI keyword scan
//   L (green)  = TBTV-AI Claude LLM
//   A (green)  = TBTV-AI for chapters (no keyword/LLM split there)
// Tooltips explain on hover for first-time viewers.
function SourceBreakdown({ by, variant }) {
  if (!by) return null
  const isKm = variant === 'km'
  const parts = isKm
    ? [
        { count: by.mux,             letter: 'M', color: '#fb923c', tip: 'Mux Robots Find Key Moments' },
        { count: by.tbtv_ai_keyword, letter: 'K', color: '#a78bfa', tip: 'TBTV-AI keyword scan (free, sport-specific vocabulary)' },
        { count: by.tbtv_ai_llm,     letter: 'L', color: '#22c55e', tip: 'TBTV-AI Claude LLM (semantic extraction)' },
      ]
    : [
        { count: by.mux,     letter: 'M', color: '#fb923c', tip: 'Mux Generate Chapters' },
        { count: by.tbtv_ai, letter: 'A', color: '#22c55e', tip: 'TBTV-AI Claude (Generate Chapters)' },
      ]
  const visible = parts.filter(p => p.count)
  if (visible.length === 0) return null
  return (
    <span style={{ marginLeft: 6, display: 'inline-flex', gap: 4, fontSize: 10, fontFamily: 'monospace' }}>
      <span style={{ color: '#555' }}>·</span>
      {visible.map((p, i) => (
        <span key={p.letter} style={{ display: 'inline-flex', gap: 4 }}>
          {i > 0 && <span style={{ color: '#444' }}>/</span>}
          <span title={`${p.count} from ${p.tip}`} style={{ color: p.color, fontWeight: 600, cursor: 'help' }}>
            {p.count}{p.letter}
          </span>
        </span>
      ))}
    </span>
  )
}

// Inline expansion of the Summary cell. Shows BOTH Mux + TBTV-AI summaries
// when present (via summary.by_source), each labeled by source. Falls back
// to the legacy single-summary shape (summary.title / description / tags)
// for rows that predate the by_source breakdown.
function ExpandedSummary({ summary }) {
  const bySource = summary?.by_source || null
  // Stable order: Mux first, TBTV-AI second, then any other source.
  const ORDER = ['mux', 'tbtv_ai', 'editor']
  const SOURCE_META = {
    mux:     { label: 'Mux Summarize',  badgeBg: 'rgba(251,146,60,0.15)', badgeColor: '#fb923c' },
    tbtv_ai: { label: 'TBTV-AI',         badgeBg: 'rgba(34,197,94,0.15)',  badgeColor: '#22c55e' },
    editor:  { label: 'Editor',          badgeBg: 'rgba(125,211,252,0.15)', badgeColor: '#7dd3fc' },
  }
  const sources = bySource
    ? Object.keys(bySource).sort((a, b) => {
        const ai = ORDER.indexOf(a); const bi = ORDER.indexOf(b)
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
      })
    : []

  // Legacy fallback — no by_source map; render the single headline summary.
  if (sources.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 980 }}>
        {summary.title && (
          <div style={{ fontSize: 14, fontWeight: 600, color: '#dbe5f5' }}>{summary.title}</div>
        )}
        {summary.description && (
          <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.55 }}>{summary.description}</div>
        )}
        {Array.isArray(summary.tags) && summary.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
            {summary.tags.map((t, i) => (
              <span key={i} style={{ fontSize: 11, padding: '1px 8px', background: '#1f2937', borderRadius: 3, color: '#bbb' }}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 980 }}>
      {sources.map(src => {
        const s = bySource[src]
        const meta = SOURCE_META[src] || { label: src, badgeBg: 'rgba(255,255,255,0.06)', badgeColor: '#aaa' }
        return (
          <div key={src} style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 10, borderLeft: `2px solid ${meta.badgeColor}55` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 10, padding: '1px 7px', borderRadius: 10,
                background: meta.badgeBg, color: meta.badgeColor, fontWeight: 700, letterSpacing: 0.3,
              }}>
                {meta.label}
              </span>
              {s.title && (
                <div style={{ fontSize: 14, fontWeight: 600, color: '#dbe5f5' }}>{s.title}</div>
              )}
            </div>
            {s.description && (
              <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.55 }}>{s.description}</div>
            )}
            {Array.isArray(s.tags) && s.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                {s.tags.map((t, i) => (
                  <span key={i} style={{ fontSize: 11, padding: '1px 8px', background: '#1f2937', borderRadius: 3, color: '#bbb' }}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Format milliseconds → "M:SS" for the inline expansion timestamps.
function fmtMs(ms) {
  if (ms == null) return '—'
  const s = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  return `${m}:${String(sec).padStart(2, '0')}`
}

// Color metadata for grouping moments/chapters by source + detector.
const ROW_SOURCE_META = {
  mux:              { label: 'Mux',                 badgeBg: 'rgba(251,146,60,0.15)',  badgeColor: '#fb923c' },
  tbtv_ai_keyword:  { label: 'TBTV-AI · keyword',   badgeBg: 'rgba(167,139,250,0.15)', badgeColor: '#a78bfa' },
  tbtv_ai_llm:      { label: 'TBTV-AI · LLM',       badgeBg: 'rgba(34,197,94,0.15)',   badgeColor: '#22c55e' },
  tbtv_ai:          { label: 'TBTV-AI',             badgeBg: 'rgba(34,197,94,0.15)',   badgeColor: '#22c55e' },
  editor:           { label: 'Editor',              badgeBg: 'rgba(125,211,252,0.15)', badgeColor: '#7dd3fc' },
}
const ROW_GROUP_ORDER = ['mux', 'tbtv_ai_keyword', 'tbtv_ai_llm', 'tbtv_ai', 'editor']

// Group an array of {source, detector, ...} rows into ordered groups so
// Mux rows appear first, then TBTV-AI keyword, then TBTV-AI LLM.
function groupBySourceDetector(rows) {
  const groups = new Map()
  for (const r of rows) {
    const key = r.source === 'tbtv_ai' && r.detector
      ? `tbtv_ai_${r.detector}`
      : (r.source || 'unknown')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(r)
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    const ai = ROW_GROUP_ORDER.indexOf(a); const bi = ROW_GROUP_ORDER.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })
}

// Loading / error / empty wrapper for the inline expansion panels.
function ExpandedShell({ entry, emptyLabel, children }) {
  if (!entry || entry.loading) {
    return <div style={{ fontSize: 12, color: '#888' }}><Loader2 size={12} className="spin" /> Loading…</div>
  }
  if (entry.error) {
    return <div style={{ fontSize: 12, color: '#dc2626' }}>Failed to load: {entry.error}</div>
  }
  if (!Array.isArray(entry.data) || entry.data.length === 0) {
    return <div style={{ fontSize: 12, color: '#888' }}>{emptyLabel}</div>
  }
  return children(entry.data)
}

// Inline expansion for the Key Moments cell. Groups moments by source +
// detector (Mux / TBTV-AI keyword / TBTV-AI LLM) and renders each block
// with its own color accent. Each moment shows timestamp + title + the
// audible narrative (the announcer's words for it).
function ExpandedKeyMoments({ entry, onPreview, onReject, onApprove, approvedSet, tbtvId, onAddMissing }) {
  // Step 27 Option B: per-row inline approve. Mirrors the existing
  // reject button styling but in green. Fingerprint match against the
  // parent's locallyApprovedSet flips the button into "Approved" state.
  const approveFp = (m) => `${m.tbtv_id || tbtvId}|${m.start_ms}|${(m.title || '').trim().toLowerCase()}`
  // Step 27 follow-up: + Add missing moment toolbar. Always visible at
  // the top of the expansion, even when there are no key moments yet.
  const addMissingBar = onAddMissing && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <button
        onClick={onAddMissing}
        title="Editor noticed a real moment the LLM missed entirely. Adds directly to the eval gold set as a positive label."
        style={{
          padding: '4px 10px', fontSize: 11,
          background: 'transparent', border: '1px solid #14532d',
          borderRadius: 4, color: '#4ade80', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
        <Plus size={12} /> Add missing moment
      </button>
      <span style={{ fontSize: 10, color: '#555' }}>
        for moments the LLM missed entirely
      </span>
    </div>
  )
  return (
    <ExpandedShell entry={entry} emptyLabel={(
      <div>
        <div style={{ marginBottom: 8 }}>No key moments stored.</div>
        {addMissingBar}
      </div>
    )}>
      {(moments) => {
        const groups = groupBySourceDetector(moments)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1100 }}>
            {addMissingBar}
            {groups.map(([key, rows]) => {
              const meta = ROW_SOURCE_META[key] || { label: key, badgeBg: 'rgba(255,255,255,0.06)', badgeColor: '#aaa' }
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 10, borderLeft: `2px solid ${meta.badgeColor}55` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 10, padding: '1px 7px', borderRadius: 10,
                      background: meta.badgeBg, color: meta.badgeColor, fontWeight: 700, letterSpacing: 0.3,
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#888' }}>{rows.length} moment{rows.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {rows.map((m, i) => (
                      <div key={`${key}-${i}`} style={{ display: 'flex', gap: 10, fontSize: 12, color: '#bbb', alignItems: 'flex-start' }}>
                        {onPreview && (
                          <button
                            onClick={() => onPreview(m)}
                            title={`Preview ${m.title || 'moment'} at ${fmtMs(m.start_ms)}`}
                            style={{
                              flexShrink: 0, padding: '2px 6px', background: '#3b82f6', border: 'none',
                              borderRadius: 3, color: 'white', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                              lineHeight: 1, marginTop: 1,
                            }}>
                            <Play size={10} />
                          </button>
                        )}
                        {onApprove && (() => {
                          const isApproved = approvedSet?.has?.(approveFp(m))
                          return (
                            <button
                              onClick={() => onApprove(m)}
                              title={isApproved
                                ? 'Already approved — feeds eval gold set + future RAG exemplars'
                                : 'Approve — mark as a real reel-worthy moment'}
                              style={{
                                flexShrink: 0, padding: '2px 6px',
                                background: isApproved ? 'rgba(34,197,94,0.15)' : 'transparent',
                                border: '1px solid ' + (isApproved ? '#16a34a' : '#14532d'),
                                borderRadius: 3,
                                color: isApproved ? '#86efac' : '#4ade80',
                                cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                                lineHeight: 1, marginTop: 1,
                              }}>
                              <CheckCircle size={10} />
                            </button>
                          )
                        })()}
                        {onReject && (
                          <button
                            onClick={() => onReject({ ...m, tbtv_id: m.tbtv_id || tbtvId })}
                            title="Reject — hide now AND skip on future TBTV-AI re-runs of this video"
                            style={{
                              flexShrink: 0, padding: '2px 6px',
                              background: 'transparent', border: '1px solid #7f1d1d',
                              borderRadius: 3, color: '#fca5a5', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                              lineHeight: 1, marginTop: 1,
                            }}>
                            <XCircle size={10} />
                          </button>
                        )}
                        <span style={{ minWidth: 56, color: '#7dd3fc', fontFamily: 'monospace' }}>{fmtMs(m.start_ms)}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                          {m.title && <div style={{ color: '#dbe5f5', fontWeight: 500 }}>{m.title}</div>}
                          {m.audible_narrative && (
                            <div style={{ color: '#aaa', lineHeight: 1.45 }}>{m.audible_narrative}</div>
                          )}
                          {m.visual_narrative && m.visual_narrative !== m.audible_narrative && (
                            <div style={{ color: '#888', fontSize: 11, fontStyle: 'italic' }}>{m.visual_narrative}</div>
                          )}
                          {(m.moment_type || m.quality_score != null || m.ground_reason) && (
                            <div style={{ fontSize: 11, color: '#666', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {m.moment_type && <span>type: <span style={{ color: '#888' }}>{m.moment_type}</span></span>}
                              {m.quality_score != null && <span>score: <span style={{ color: '#888' }}>{m.quality_score.toFixed(2)}</span></span>}
                              {m.ground_reason === 'llm_cue_citation' && (
                                <span title={`Grounded to cue_id${Array.isArray(m.grounded_cue_ids) && m.grounded_cue_ids.length > 1 ? 's' : ''} ${Array.isArray(m.grounded_cue_ids) ? m.grounded_cue_ids.join(', ') : ''}`}
                                  style={{ color: '#22c55e' }}>
                                  🔗 grounded ({Array.isArray(m.grounded_cue_ids) ? m.grounded_cue_ids.length : 0})
                                </span>
                              )}
                              {m.ground_reason === 'keyword_match' && Array.isArray(m.grounded_cue_ids) && m.grounded_cue_ids[0] != null && (
                                <span title={`Keyword matched cue_id ${m.grounded_cue_ids[0]}`} style={{ color: '#a78bfa' }}>
                                  🔗 cue {m.grounded_cue_ids[0]}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )
      }}
    </ExpandedShell>
  )
}

// Inline expansion for the Chapters cell. Groups by source (Mux vs TBTV-AI)
// and lists each chapter as timestamp + title.
function ExpandedChapters({ entry, onPreview }) {
  return (
    <ExpandedShell entry={entry} emptyLabel="No chapters stored.">
      {(chapters) => {
        const groups = groupBySourceDetector(chapters)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1100 }}>
            {groups.map(([key, rows]) => {
              const meta = ROW_SOURCE_META[key] || { label: key, badgeBg: 'rgba(255,255,255,0.06)', badgeColor: '#aaa' }
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 10, borderLeft: `2px solid ${meta.badgeColor}55` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 10, padding: '1px 7px', borderRadius: 10,
                      background: meta.badgeBg, color: meta.badgeColor, fontWeight: 700, letterSpacing: 0.3,
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#888' }}>{rows.length} chapter{rows.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {rows.map((c, i) => (
                      <div key={`${key}-${i}`} style={{ display: 'flex', gap: 10, fontSize: 12, alignItems: 'center' }}>
                        {onPreview && (
                          <button
                            onClick={() => onPreview(c)}
                            title={`Jump to ${c.title || 'chapter'} at ${fmtMs(c.start_ms)}`}
                            style={{
                              flexShrink: 0, padding: '2px 6px', background: '#3b82f6', border: 'none',
                              borderRadius: 3, color: 'white', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                              lineHeight: 1,
                            }}>
                            <Play size={10} />
                          </button>
                        )}
                        <span style={{ minWidth: 56, color: '#7dd3fc', fontFamily: 'monospace' }}>{fmtMs(c.start_ms)}</span>
                        <span style={{ color: '#dbe5f5' }}>{c.title || '(untitled chapter)'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )
      }}
    </ExpandedShell>
  )
}


// Step 27 UI option B: compact per-video coverage indicator. Shows
// editorial review state at a glance — how many AI moments have been
// approved (✓) vs rejected (✗) for this video. Greyed-out when both are
// zero (unreviewed). Editor uses this to spot videos that need attention.
function CoverageCell({ approved, dropped }) {
  const total = approved + dropped
  if (total === 0) {
    return (
      <span style={{ color: '#555', fontSize: 11 }}>—</span>
    )
  }
  return (
    <div style={{ display: 'inline-flex', gap: 8, fontSize: 11, fontFamily: 'monospace' }}>
      <span style={{ color: '#4ade80' }} title={`${approved} approved`}>✓{approved}</span>
      <span style={{ color: '#fca5a5' }} title={`${dropped} rejected`}>✗{dropped}</span>
    </div>
  )
}
